// src/store/notification.store.ts
import { defineStore } from 'pinia'
import axios from 'axios'
import { getCachedBundle, isSupportedLocale, type Locale } from '@/i18n/bundle'
import { extractApiError } from '@/utils/apiError'

export type Notification = {
  id: number | null
  title: string
  body: string
  url: string
  idempotencyKey: string
  occurredAt: number | null
  createdAt: number | null
  readAt: number | null
}

type NotificationDTO = {
  id?: number
  title?: unknown
  body?: unknown
  url?: unknown
  idempotency_key?: unknown
  idempotencyKey?: unknown
  occurred_at?: unknown
  occurredAt?: unknown
  created_at?: unknown
  createdAt?: unknown
  read_at?: unknown
  readAt?: unknown
}

let inflight: Promise<void> | null = null

function toMs(d: unknown): number | null {
  if (!d) return null
  if (d instanceof Date && !isNaN(d.getTime())) return d.getTime()
  if (typeof d === 'number') return d > 1e12 ? d : d * 1000

  if (typeof d === 'string') {
    const n = Number(d)
    if (!Number.isNaN(n)) return n > 1e12 ? n : n * 1000

    const t = Date.parse(d)
    return Number.isNaN(t) ? null : t
  }

  return null
}

const MESSAGE_KEY_REGEX = /\bmessages?\.[\w.]+\b/g

function getMessagesMap(): Record<string, string> | null {
  const htmlLang = document.documentElement.getAttribute('lang')
  const locale: Locale = isSupportedLocale(htmlLang) ? (htmlLang as Locale) : 'de'

  const bundle = getCachedBundle(locale)
  const messages = bundle?.messages

  if (!bundle || !messages || typeof messages !== 'object') {
    return null
  }

  return messages as Record<string, string>
}

function translateText(raw: string): string {
  if (!raw || typeof raw !== 'string') return raw

  const messages = getMessagesMap()
  if (!messages) return raw

  let result = raw

  if (result.includes('messages.') || result.includes('message.')) {
    result = result.replace(MESSAGE_KEY_REGEX, fullKey => {
      const stripped = fullKey.replace(/^messages?\./, '')
      const lastSegment = stripped.split('.').pop() || stripped
      const translated = messages[stripped] ?? messages[fullKey] ?? messages[lastSegment]

      return translated ?? fullKey
    })
  }

  if (result === raw) {
    const trimmed = result.trim()
    const lastSegment = trimmed.split('.').pop() || trimmed
    const translated = messages[trimmed] ?? messages[lastSegment] ?? null

    if (translated) result = translated
  }

  return result
}

function normalize(dto: NotificationDTO): Notification {
  const id = typeof dto.id === 'number' ? dto.id : null

  const title = translateText(String(dto?.title ?? ''))
  const body = translateText(String(dto?.body ?? ''))
  const url = String(dto?.url ?? '')

  const keyRaw = (dto as any).idempotencyKey ?? (dto as any).idempotency_key
  const idempotencyKey = keyRaw == null ? '' : String(keyRaw).trim()

  const occurredAt = toMs((dto as any).occurredAt ?? (dto as any).occurred_at) ?? null
  const createdAt = toMs((dto as any).createdAt ?? (dto as any).created_at) ?? null
  const readAt = toMs((dto as any).readAt ?? (dto as any).read_at) ?? null

  return {
    id,
    title,
    body,
    url,
    idempotencyKey,
    occurredAt,
    createdAt,
    readAt
  }
}

function isValid(n: Notification): boolean {
  return n.title.length > 0 && n.url.length > 0 && n.idempotencyKey.length > 0
}

function sortDesc(a: Notification, b: Notification): number {
  const oa = a.occurredAt ?? -Infinity
  const ob = b.occurredAt ?? -Infinity

  if (oa !== ob) return ob - oa

  const ca = a.createdAt ?? -Infinity
  const cb = b.createdAt ?? -Infinity

  if (ca !== cb) return cb - ca

  const ia = a.id ?? -Infinity
  const ib = b.id ?? -Infinity

  return (ib as number) - (ia as number)
}

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    notifications: [] as Notification[],
    loaded: false,
    lastError: null as string | null
  }),

  getters: {
    unreadNotifications: state => state.notifications.filter(n => !n.readAt),
    unreadCount: state => state.notifications.filter(n => !n.readAt).length
  },

  actions: {
    setError(msg: string | null) {
      this.lastError = msg
    },

    $reset() {
      this.notifications = []
      this.loaded = false
      this.lastError = null
    },

    async fetchNotifications(forceFetch = false) {
      if (this.loaded && !forceFetch) return
      if (inflight && !forceFetch) return inflight

      inflight = (async () => {
        this.setError(null)

        try {
          const { data } = await axios.get('/notifications', {
            withCredentials: true
          })

          const list: NotificationDTO[] = Array.isArray(data)
              ? data
              : Array.isArray(data?.data?.notifications)
                  ? data.data.notifications
                  : []

          const seen = new Set<string>()
          const ready: Notification[] = []

          for (const dto of list) {
            const n = normalize(dto)

            if (!isValid(n)) continue
            if (seen.has(n.idempotencyKey)) continue

            seen.add(n.idempotencyKey)
            ready.push(n)
          }

          ready.sort(sortDesc)

          this.notifications = ready
          this.loaded = true
        } catch (error) {
          const { message } = extractApiError(error)
          this.setError(message)
        } finally {
          inflight = null
        }
      })()

      return inflight
    },

    async addNotification(dto: NotificationDTO): Promise<boolean> {
      this.setError(null)

      try {
        const n = normalize(dto)

        if (!isValid(n)) {
          return false
        }

        const idx = this.notifications.findIndex(
            x => x.idempotencyKey === n.idempotencyKey
        )

        if (idx !== -1) {
          const prev = this.notifications[idx]

          this.notifications[idx] = {
            ...prev,
            ...n,
            readAt: prev.readAt ?? n.readAt ?? null
          }
        } else {
          this.notifications.push(n)
        }

        this.notifications.sort(sortDesc)

        return true
      } catch (error) {
        const { message } = extractApiError(error)
        this.setError(message)

        return false
      }
    },

    async markAsRead(idempotencyKey: string): Promise<boolean> {
      this.setError(null)

      if (!idempotencyKey) return true

      const index = this.notifications.findIndex(
          n => n.idempotencyKey === idempotencyKey
      )

      const previousReadAt = index !== -1 ? this.notifications[index].readAt : null

      if (index !== -1) {
        this.notifications[index].readAt = Date.now()
      }

      try {
        await axios.patch(
            '/notifications/read',
            { idempotency_key: idempotencyKey },
            {
              withCredentials: true,
              validateStatus: status =>
                  status === 404 || (status >= 200 && status < 300)
            }
        )

        return true
      } catch (error) {
        const { message } = extractApiError(error)
        this.setError(message)

        if (index !== -1) {
          this.notifications[index].readAt = previousReadAt
        }

        return false
      }
    },

    async removeNotification(idempotencyKey: string): Promise<boolean> {
      return this.removeNotifications([idempotencyKey])
    },

    async removeNotifications(idempotencyKeys: string[]): Promise<boolean> {
      this.setError(null)

      const keys = [...new Set(idempotencyKeys.filter(Boolean))]
      if (!keys.length) return true

      const before = [...this.notifications]

      this.notifications = this.notifications.filter(
          n => !keys.includes(n.idempotencyKey)
      )

      try {
        await axios.delete('/notifications', {
          data: { idempotency_keys: keys },
          withCredentials: true,
          validateStatus: status =>
              status === 404 ||
              status === 202 ||
              status === 200 ||
              status === 204
        })

        return true
      } catch (error) {
        const { message } = extractApiError(error)
        this.setError(message)

        this.notifications = before

        return false
      }
    },

    clearNotifications() {
      this.notifications = []
      this.setError(null)
    }
  }
})
